import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { clsx } from 'clsx';
import { Check } from 'lucide-react';
import { useEffect, useState, type CSSProperties } from 'react';

import { COMPANIES, type CompanyCode } from '@dcc-erp/shared';

import { clearPendingLoginCompany, savePendingLoginCompany, useAuth } from '../lib/auth';

export const Route = createFileRoute('/login')({
  component: LoginPage,
});

function LoginPage() {
  const navigate = useNavigate();
  const { login, isAuthenticated, isLoading } = useAuth();
  const [company, setCompany] = useState<CompanyCode>('DCC');
  const [submitting, setSubmitting] = useState(false);

  const co = COMPANIES.find((c) => c.code === company)!;

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      void navigate({ to: '/dashboard' });
    }
  }, [isAuthenticated, isLoading, navigate]);

  useEffect(() => {
    document.documentElement.setAttribute('data-company', company);
  }, [company]);

  // When the user hits Back from the Entra page, the browser restores this page
  // from the bfcache with its JS state intact — so `submitting` is still true and
  // the button stays stuck on "กำลังเปลี่ยนเส้นทาง…", disabled. Reset it on a
  // bfcache restore (pageshow.persisted) so the button is usable again.
  useEffect(() => {
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) setSubmitting(false);
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  const submit = async () => {
    setSubmitting(true);
    // Remember the company picked above so it becomes the active company after the
    // Entra round-trip — the UUID it maps to isn't known until /companies loads
    // post-auth (see useApplyPendingLoginCompany).
    savePendingLoginCompany(company);
    try {
      await login();
    } catch (err) {
      // loginRedirect failed locally (never reached Entra) — drop the stash so a
      // later sign-in doesn't silently apply this abandoned selection.
      clearPendingLoginCompany();
      console.error('[auth] loginRedirect failed', err);
      setSubmitting(false);
    }
  };

  // After the redirect fix, normal sign-in no longer routes through /login — but
  // an authenticated session can still land here via other paths (the 401
  // hard-redirect in api.ts, a bookmarked /login, or / → /login). Render a loader
  // instead of the form while auth is resolving or already established, so the
  // login form never flashes before the bounce to /dashboard.
  if (isLoading || isAuthenticated) {
    return (
      <div className="login-shell">
        <div className="muted" style={{ textAlign: 'center', padding: 32 }}>
          กำลังโหลด…
        </div>
      </div>
    );
  }

  return (
    <div className="login-shell">
      <div className="login-main">
        <section className="login-brand-panel" aria-label="DCC ERP">
          <div className="login-brand-mark">{co.code}</div>
          <div>
            <div className="login-brand-kicker">ERP Workspace</div>
            <h1>DCC ERP</h1>
          </div>
          <div className="login-active-company">
            <span>บริษัทที่เลือก</span>
            <strong>{co.nameTh}</strong>
            <small>{co.nameEn}</small>
          </div>
        </section>

        <div className="login-card">
          <div className="login-card-header">
            <div className="login-card-heading">
              <h2>เข้าสู่ระบบ</h2>
              <p>เลือกบริษัทแล้วลงชื่อเข้าใช้ด้วยบัญชี Microsoft</p>
            </div>
          </div>

          <div className="login-co-section">
            <div className="login-co-label">เลือกบริษัท</div>
            <div className="login-co-grid company-grid" role="group" aria-label="เลือกบริษัท">
              {COMPANIES.map((c) => {
                const selected = company === c.code;
                return (
                  <button
                    key={c.code}
                    type="button"
                    className={clsx(
                      'login-co-tile',
                      'co-tile',
                      c.code.toLowerCase(),
                      selected && 'active',
                      selected && 'selected',
                    )}
                    style={{ '--tile-color': c.color } as CSSProperties}
                    aria-pressed={selected}
                    onClick={() => setCompany(c.code)}
                  >
                    <div className="login-co-tile-top">
                      <div className="login-co-badge" style={{ background: c.color }}>
                        {c.code}
                      </div>
                      <div className={clsx('login-co-check', selected && 'visible')}>
                        <Check size={16} strokeWidth={3.25} aria-hidden="true" />
                      </div>
                    </div>
                    <div className="login-co-name">{c.nameTh}</div>
                    <div className="login-co-en">{c.nameEn}</div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="login-submit-wrap">
            <button
              type="button"
              onClick={() => void submit()}
              disabled={submitting || isLoading}
              className="login-submit-btn"
            >
              <MicrosoftLogo />
              <span>{submitting ? 'กำลังเปลี่ยนเส้นทาง…' : 'เข้าสู่ระบบด้วย Microsoft'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function MicrosoftLogo() {
  return (
    <svg width="22" height="22" viewBox="0 0 23 23" aria-hidden focusable="false">
      <rect x="1" y="1" width="10" height="10" fill="#f25022" />
      <rect x="12" y="1" width="10" height="10" fill="#7fba00" />
      <rect x="1" y="12" width="10" height="10" fill="#00a4ef" />
      <rect x="12" y="12" width="10" height="10" fill="#ffb900" />
    </svg>
  );
}
